import {
	baseApiHeaders,
	HIDE_TOAST_KEY,
	type IActivateAuthenticator2fa,
	type IApiResponse,
	type IAuthSettings,
	type IFeatureNote,
	type IInitiateAuthenticator2Fa,
	type IVIPStats,
} from 'BreetHelpers';

import { setAuthSettings } from '../app';
import { apiSlice } from './apiSlice';

export const settingsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getSettings: builder.query<IApiResponse<IAuthSettings>, void>({
			query: () => ({ url: '/auth/settings' }),
			onQueryStarted: async (_userId, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(setAuthSettings(data.data));
				} catch (error) {
					console.error(error);
				}
			},
		}),
		generateOTP: builder.query<IApiResponse, void>({
			query: () => ({ url: '/users/otp/generate', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			providesTags: ['GenerateOTP'],
		}),
		verifyOTP: builder.mutation<IApiResponse, { otp: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/users/otp/validate',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		createPin: builder.mutation<IApiResponse, { pin: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/users/user/pincode',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		verifyPin: builder.query<IApiResponse, { pin: string }>({
			query: (body) => ({
				method: 'PATCH',
				url: '/users/user/pincode/verify',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		updatePin: builder.mutation<IApiResponse, { pin: string; oldPin: string }>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/user/pincode',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		resetPin: builder.mutation<IApiResponse, { pin: string; otp: string }>({
			query: (body) => ({
				method: 'PATCH',
				url: '/users/user/pincode',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		getVIPStats: builder.query<IApiResponse<IVIPStats>, Record<string, string> | undefined>({
			query: (params) => ({ url: '/users/user/vip-stats', params }),
		}),
		getFeatureNote: builder.query<IApiResponse<IFeatureNote[]>, string | undefined>({
			query: () => ({ url: '/users/notes' }),
		}),
		enable2faEmail: builder.mutation<IApiResponse, void>({
			query: (body) => ({
				method: 'POST',
				url: '/users/user/2fa/email/activation',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		disable2faEmail: builder.mutation<IApiResponse, { otp: string } | void>({
			query: (body) => ({
				method: 'PATCH',
				url: '/users/user/2fa/email/deactivation',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		initiate2faAuthenticator: builder.mutation<IApiResponse<IInitiateAuthenticator2Fa>, void>({
			query: () => ({
				method: 'POST',
				url: '/users/user/2fa/authenticator/initiation',
				headers: {
					...baseApiHeaders,

					'Cache-Control': 'no-store, no-cache, must-revalidate',
					'Pragma': 'no-cache',
					'Expires': '0',
				},
				cache: 'no-store',
			}),
		}),
		activate2faAuthenticator: builder.mutation<IApiResponse<IActivateAuthenticator2fa>, { otp: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/users/user/2fa/authenticator/activation',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		reActivate2faAuthenticator: builder.mutation<IApiResponse, { otp: string } | void>({
			query: (body) => ({
				method: 'POST',
				url: '/users/user/2fa/authenticator/re-activation',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		deActivate2faAuthenticator: builder.mutation<IApiResponse, { otp: string } | void>({
			query: (body) => ({
				method: 'POST',
				url: '/users/user/2fa/authenticator/deactivation',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference'],
		}),
		suggest2fa: builder.query<IApiResponse<{ showSuggestion: boolean }>, void>({
			query: () => ({
				method: 'GET',
				url: '/users/user/2fa/suggestions',
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
	}),
});

export const {
	useLazyGenerateOTPQuery,
	useGenerateOTPQuery,
	useVerifyOTPMutation,
	useGetVIPStatsQuery,
	useLazyGetVIPStatsQuery,
	useGetSettingsQuery,
	useCreatePinMutation,
	useUpdatePinMutation,
	useVerifyPinQuery,
	useLazyVerifyPinQuery,
	useGetFeatureNoteQuery,
	useResetPinMutation,
	useEnable2faEmailMutation,
	useDisable2faEmailMutation,
	useInitiate2faAuthenticatorMutation,
	useActivate2faAuthenticatorMutation,
	useReActivate2faAuthenticatorMutation,
	useDeActivate2faAuthenticatorMutation,
	useSuggest2faQuery,
} = settingsApiSlice;
